import React, { useState } from 'react'
import {
    Autocomplete,
    Box,
    Chip,
    CircularProgress,
    Divider,
    IconButton,
    Paper,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { getStandardRightActions, TopBar } from '../../topbar/top-bar'
import { BottomPageNavigation } from '../../bottom-navigation/bottom-navigation'
import { KotiContainer } from '../../general/layout/koti-container'
import { RawTenantData, useGetRawTenantDataQuery } from '../products.service'

// ─── CopyList ────────────────────────────────────────────────────────────────

const CopyList: React.FC<{ label: string; items: string[] }> = ({ label, items }) => {
    const [copied, setCopied] = useState(false)

    const copyAll = () => {
        navigator.clipboard.writeText(items.join('\n'))
        setCopied(true)
        setTimeout(() => setCopied(false), 1500)
    }

    if (!items?.length) return null

    return (
        <Paper variant="outlined" sx={{ p: 2 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="subtitle2" fontWeight="bold">
                    {label}{' '}
                    <Typography component="span" variant="caption" color="text.secondary">
                        ({items.length})
                    </Typography>
                </Typography>
                <Tooltip title={copied ? 'Kopiert!' : 'Kopier alle'}>
                    <IconButton size="small" onClick={copyAll}>
                        <ContentCopyIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>
            <Box display="flex" flexWrap="wrap" gap={0.75}>
                {items.map((item) => (
                    <Chip key={item} label={item} size="small" variant="outlined" sx={{ fontFamily: 'monospace' }} />
                ))}
            </Box>
        </Paper>
    )
}

// ─── CopyMap ─────────────────────────────────────────────────────────────────

const CopyMap: React.FC<{ label: string; entries: Record<string, string> }> = ({ label, entries }) => {
    const [copied, setCopied] = useState(false)
    const pairs = Object.entries(entries ?? {})

    const copyAll = () => {
        navigator.clipboard.writeText(pairs.map(([k, v]) => `${k}\t${v}`).join('\n'))
        setCopied(true)
        setTimeout(() => setCopied(false), 1500)
    }

    if (!pairs.length) return null

    return (
        <Paper variant="outlined" sx={{ p: 2 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="subtitle2" fontWeight="bold">
                    {label}{' '}
                    <Typography component="span" variant="caption" color="text.secondary">
                        ({pairs.length})
                    </Typography>
                </Typography>
                <Tooltip title={copied ? 'Kopiert!' : 'Kopier alle (kode → navn)'}>
                    <IconButton size="small" onClick={copyAll}>
                        <ContentCopyIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>
            <Stack spacing={0.5}>
                {pairs.map(([key, value]) => (
                    <Box key={key} display="flex" alignItems="center" gap={1}>
                        <Typography
                            variant="caption"
                            sx={{ fontFamily: 'monospace', minWidth: 60, color: 'text.secondary' }}
                        >
                            {key}
                        </Typography>
                        <Typography variant="caption">{value}</Typography>
                    </Box>
                ))}
            </Stack>
        </Paper>
    )
}

// ─── TenantDetail ────────────────────────────────────────────────────────────

const TenantDetail: React.FC<{ tenant: RawTenantData }> = ({ tenant }) => (
    <Stack spacing={2} mt={2}>
        {/* Basic info — no copy buttons, short values */}
        <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold" mb={1}>
                Info
            </Typography>
            <Stack spacing={0.5}>
                {(
                    [
                        ['ID', tenant.id],
                        ['TenantId', tenant.tenantId],
                        ['E-post', tenant.email],
                        ['Telefon', tenant.phone],
                    ] as [string, string][]
                ).map(([label, value]) => (
                    <Box key={label} display="flex" gap={2}>
                        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 80 }}>
                            {label}
                        </Typography>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                            {value}
                        </Typography>
                    </Box>
                ))}
            </Stack>
        </Paper>

        <Divider />

        <CopyList label="Kommuner (full)" items={tenant.municipalities} />
        <CopyList label="Delkommuner (partial)" items={tenant.partialMunicipalities} />
        <CopyList label="Postnummer" items={tenant.postalCodes} />
        <CopyList label="Delvise postnummer (partial)" items={tenant.partialPostalCodes} />
        <CopyMap label="Kommunekart (kode → navn)" entries={tenant.municipalitiesMap} />
    </Stack>
)

// ─── Main view ───────────────────────────────────────────────────────────────

export const TenantDataAdminView: React.FC = () => {
    const { data: tenants, isLoading } = useGetRawTenantDataQuery()
    const [selected, setSelected] = useState<RawTenantData | null>(null)

    return (
        <div>
            <TopBar rightActions={getStandardRightActions()} />
            <KotiContainer>
                <Typography variant="h6" mb={2}>
                    Tenant Data (cached)
                </Typography>

                {isLoading ? (
                    <CircularProgress />
                ) : (
                    <Autocomplete<RawTenantData>
                        options={tenants ?? []}
                        getOptionLabel={(t) => t.name}
                        value={selected}
                        onChange={(_, val) => setSelected(val)}
                        renderInput={(params) => <TextField {...params} label="Velg tenant" size="small" />}
                        sx={{ maxWidth: 400 }}
                    />
                )}

                {selected && <TenantDetail tenant={selected} />}
            </KotiContainer>
            <BottomPageNavigation />
        </div>
    )
}
